import { TFunction } from 'i18next';
import { ObjectSchema } from 'yup';
import { dateSeperator } from '../../../config';
import {
  entityLegalFormOptions,
  genderOptions,
  milesCoCustomerRelationOptions,
  professionOptions,
  telephoneCountryCodesOptions,
} from '../../../resources/base/models';
import { BooleanEnum } from '../../../resources/base/models/enums';
import { dateFormat } from '../../../services/common/format';
import { getEnumValues, getOptionsListValues, strBoolToBool } from '../../../services/common/miscUtils';
import { phoneValidationUtils, Yup } from '../../../services/common/validation';
import { validateEmail, validateMobilePhone } from '../../../services/common/validationUtils';
import {
  BusinessCustomerProspectForm,
  PrivateCoCustomerProspectForm,
  PrivateCustomerProspectForm,
} from './initialValues';

const regexNumeric = /^[0-9]+$/;
const regexName =
  /^[a-zA-ZÄÅáâäàãåÉéêëèíîïìÖóôöòõÜúüûùÇçÑñŠšß'\\.-][a-zA-ZÄÅáâäàãåÉéêëèíîïìÖóôöòõÜúüûùÇçÑñŠšß '\\.-]+$/;
const regexNameCompany =
  /^[a-zA-ZÄÅáâäàãåÉéêëèíîïìÖóôöòõÜúüûùÇçÑñŠšß\-\\.!'@#$%^&*()_?0-9][a-zA-ZÄÅáâäàãåÉéêëèíîïìÖóôöòõÜúüûùÇçÑñŠš ß\-\\.!'@#$%^&*()_?0-9]+$/;
const regexPreviousContractNumber = /^\d{8}[A-Za-z]{3}\d?$/;

let _t = (() => '') as TFunction;
// eslint-disable-next-line prettier/prettier
const t =
  (...args: Parameters<TFunction>) =>
  () =>
    _t(...args);

const gender = Yup.string()
  .required(t('validation:requiredSelect'))
  .oneOf(getOptionsListValues(genderOptions), t('validation:required'));
const firstName = Yup.string()
  .test('no-edge-spaces', t('validation:spaceIssue'), (value) => !!value && value === value.trim())
  .min(2, t('validation:minLength', { value: 2 }))
  .max(120, t('validation:maxLength', { value: 120 }))
  .matches(regexName, t('validation:invalidCharacter'))
  .required(t('validation:required'));
const lastName = Yup.string()
  .test('no-edge-spaces', t('validation:spaceIssue'), (value) => !!value && value === value.trim())
  .min(2, t('validation:minLength', { value: 2 }))
  .max(120, t('validation:maxLength', { value: 120 }))
  .matches(regexName, t('validation:invalidCharacter'))
  .required(t('validation:required'));
const birthName = Yup.string()
  .test('no-edge-spaces', t('validation:spaceIssue'), (value) => !!value && value === value.trim())
  .min(2, t('validation:minLength', { value: 2 }))
  .max(120, t('validation:maxLength', { value: 120 }))
  .matches(regexName, t('validation:invalidCharacter'))
  .required(t('validation:required'));
const email = validateEmail();
const mobilePhone = validateMobilePhone();
const telephonePhone = Yup.string()
  .matches(phoneValidationUtils.landline.regexWithPrefix, t('validation:invalidCharacter'))
  .transform((value) => value.replace(/\s+/g, ''))
  .landlinePhoneLength();
const dateOfBirth = Yup.string()
  .required(t('validation:requiredSelect'))
  .dateFormat(dateFormat, dateSeperator, t('validation:invalidDateformat'))
  .notInFuture(dateSeperator, t('validation:dateInFuture'))
  .fullAge(18, dateSeperator, t('validation:fullAge'));
const profession = Yup.string()
  .required(t('validation:requiredSelect'))
  .oneOf(getOptionsListValues(professionOptions), t('validation:required'));

const previousContractNumber = Yup.string().when('isExistingClient', {
  is: (value: BooleanEnum) => strBoolToBool(value),
  then: (schema) =>
    schema
      .required(t('validation:required'))
      .max(12, t('validation:maxLength', { value: 12 }))
      .matches(regexPreviousContractNumber, t('validation:invalidCharacter')),
});

const carToBeReturned = Yup.boolean().required(t('validation:required'));

const companyCreationDate = Yup.string()
  .required(t('validation:requiredSelect'))
  .dateFormat(dateFormat, dateSeperator, t('validation:invalidDateformat'))
  .notInFuture(dateSeperator, t('validation:dateInFuture'));

const customerRelations = Yup.string()
  .required(t('validation:requiredSelect'))
  .oneOf(getOptionsListValues(milesCoCustomerRelationOptions), t('validation:required'));

const sirenWebserviceCall = Yup.string()
  .matches(regexNumeric, t('validation:invalidCharacter'))
  .required(t('validation:required'))
  .min(9, t('validation:minLength', { value: 9 }))
  .max(9, t('validation:maxLength', { value: 9 }));
const apecode = Yup.string()
  .required(t('validation:required'))
  .matches(/^[0-9]{4}[A-Z]{1}$/, t('validation:invalidCharacter'));
const companyName = Yup.string()
  .test('no-edge-spaces', t('validation:spaceIssue'), (value) => !!value && value === value.trim())
  .required(t('validation:required'))
  .matches(regexNameCompany, t('validation:invalidCharacter'))
  .min(2, t('validation:minLength', { value: 2 }))
  .max(120, t('validation:maxLength', { value: 120 }));
const privateUserCheck = Yup.boolean().required();

const legalEntity = Yup.string()
  .required(t('validation:required'))
  .oneOf(getOptionsListValues(entityLegalFormOptions), t('validation:required'));

const mobilePhoneCountryCode = Yup.string()
  .oneOf(getOptionsListValues(telephoneCountryCodesOptions), t('validation:invalidData'))
  .required(t('validation:required'));
const isExistingClient = Yup.string()
  .oneOf(getEnumValues(BooleanEnum), t('validation:invalidData'))
  .required(t('validation:required'));
const hasCoCustomer = Yup.boolean().default(false);

export const getPrivateCustomerValidationSchema = (__t: TFunction): ObjectSchema<PrivateCustomerProspectForm> => {
  _t = __t;

  return Yup.object({
    gender,
    firstName,
    lastName,
    birthName,
    dateOfBirth,
    profession,
    email,
    telephonePhone,
    mobilePhone,
    mobilePhoneCountryCode,
    isExistingClient,
    previousContractNumber,
    carToBeReturned,
    hasCoCustomer,
  });
};

export const getPrivateCoCustomerValidationSchema = (__t: TFunction): ObjectSchema<PrivateCoCustomerProspectForm> => {
  _t = __t;

  return Yup.object({
    gender,
    firstName,
    lastName,
    birthName,
    dateOfBirth,
    profession,
    email,
    telephonePhone,
    mobilePhone,
    mobilePhoneCountryCode,
    customerRelations,
  });
};

export const getBusinessCustomerValidationSchema = (
  isSoleTrader: boolean,
  __t: TFunction
): ObjectSchema<BusinessCustomerProspectForm> => {
  _t = __t;

  return Yup.object({
    sirenWebserviceCall,
    legalEntity,
    companyName,
    apecode,
    companyCreationDate,
    privateUserCheck,
    mobilePhoneCountryCode,
    mobilePhone,
    telephonePhone,
    email,
    isExistingClient,
    previousContractNumber,
    carToBeReturned,
    dateOfBirth: isSoleTrader ? dateOfBirth : Yup.mixed(),
    firstName: isSoleTrader ? firstName : Yup.mixed(),
    lastName: isSoleTrader ? lastName : Yup.mixed(),
    // birthName:  isSoleTrader ? birthName   : Yup.mixed(), // TODO
    gender: isSoleTrader ? gender : Yup.mixed(),
  });
};
